import { DateTime } from 'luxon'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import translationKeys from '@translationKeys'

import { CustomTooltip } from '@components/CustomTooltip'
import TruncateLines from '@components/TruncateLines'
import Button from '@components/formControls/Button'
import Tooltip, { tooltipClasses } from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'

import EventsTooltip from './EventsTooltip'
import { StyledContainer } from './index.styles'
import { EventsWithPopoverProps } from './types'

const EventsWithPopover = ({ profileData, attendingEvents, isSubscriber }: EventsWithPopoverProps) => {
  const { t } = useTranslation()
  const upcomingEvents = profileData.profileEvents || []
  const sortedUpcomingEvents = upcomingEvents
    .filter(event => DateTime.fromISO(event.endDate).startOf('day') >= DateTime.now().startOf('day'))
    .sort((a, b) => DateTime.fromISO(a.endDate).valueOf() - DateTime.fromISO(b.endDate).valueOf())

  const isAttendingFirstEvent = isSubscriber && attendingEvents.includes(sortedUpcomingEvents?.[0]?.id)
  const attendingEventNames = sortedUpcomingEvents.filter(event => attendingEvents.includes(event.id)).map(({ name }) => name)
  const notAttendingEventNames = sortedUpcomingEvents.filter(event => !attendingEvents.includes(event.id)).map(({ name }) => name)

  const title = useMemo(() => {
    if (!isSubscriber || !attendingEventNames.length) {
      return null
    }
    if (isAttendingFirstEvent && sortedUpcomingEvents.length === 1) {
      return <Typography variant="body2">{t(translationKeys.CUSTOM_LIST_TABLE_TOOLTIP_BOTH_ATTENDING_SAME)}</Typography>
    }
    return <EventsTooltip attendingEventNames={attendingEventNames} notAttendingEventNames={notAttendingEventNames} />
  }, [])

  if (sortedUpcomingEvents.length === 0) {
    return <Typography />
  }

  const eventsList = (
    <>
      {sortedUpcomingEvents.map(event => (
        <Typography key={event.id} style={{ margin: '5px 0', fontWeight: '700' }}>
          {event.name}
        </Typography>
      ))}
    </>
  )

  const firstEventName = sortedUpcomingEvents[0]?.name

  const showMoreButton =
    sortedUpcomingEvents.length > 1 && isSubscriber ? (
      <Tooltip
        title={eventsList}
        PopperProps={{
          sx: {
            [`& .${tooltipClasses.tooltip}`]: {
              marginTop: '0px',
              minWidth: '238px',
              maxWidth: '100%',
              height: 'auto',
              padding: '12px',
              borderRadius: '4px',
              backgroundColor: '#ffff',
              boxShadow: '0px 0px 5px rgba(0, 0, 0, 0.12)',
            },
          },
        }}
        placement="bottom-start"
      >
        <Button
          style={{
            cursor: 'pointer',
            backgroundColor: 'transparent',
            border: 'none',
            padding: 0,
            display: 'block',
            minHeight: '15px',
          }}
        >
          {t(translationKeys.CUSTOM_LIST_TABLE_BTN_FUTURE_EVENTS)}
        </Button>
      </Tooltip>
    ) : null

  return (
    <StyledContainer
      display="flex"
      alignItems="center"
      flexWrap="wrap"
      width="100%"
      isHighlighted={isSubscriber && !!attendingEventNames.length}
    >
      <CustomTooltip title={title} width="100%">
        <div>
          <TruncateLines lines={4}>
            <Typography style={{ wordBreak: 'break-all', fontWeight: 700 }}>{firstEventName}</Typography>
          </TruncateLines>
        </div>
      </CustomTooltip>
      {showMoreButton}
    </StyledContainer>
  )
}

export default EventsWithPopover
